/**
 * API 工具函数 - 基于新的数据库架构
 */

import { supabase } from './supabase';
import type { LiveSession, Danmaku, Screenshot, AddSessionForm, SentimentAnalysisResult, ScheduleRule, CreateScheduleRuleForm } from '@/types';
import { scrapeRoomInfo } from './backend-api';
import type { SessionExportData } from './csvExport';

// ============= 直播场次相关 =============

/**
 * 获取所有直播场次（按时间倒序）
 */
export const getAllSessions = async (): Promise<LiveSession[]> => {
  const { data, error } = await supabase
    .from('bilibili_live_sessions')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('获取场次列表失败:', error);
    throw error;
  }

  return data || [];
};

export interface LatestDanmakuEvidence {
  timestamp: number | null;
  created_at: string | null;
}

/**
 * 获取指定场次最近一条普通弹幕的时间证据。
 * 只读取时间字段，避免首页加载或暴露弹幕正文。
 */
export const getLatestDanmakuEvidence = async (
  sessionId: string
): Promise<LatestDanmakuEvidence | null> => {
  const { data, error } = await supabase
    .from('bilibili_danmaku')
    .select('timestamp, created_at')
    .eq('session_id', sessionId)
    .eq('message_type', 'danmaku')
    .order('timestamp', { ascending: false })
    .limit(1);

  if (error) {
    console.error('获取最近弹幕时间失败:', error);
    throw error;
  }

  const latest = data?.[0];
  if (!latest) return null;

  return {
    timestamp: typeof latest.timestamp === 'number' ? latest.timestamp : null,
    created_at: typeof latest.created_at === 'string' ? latest.created_at : null,
  };
};

/**
 * 获取指定状态的场次列表
 * @param status 场次状态：'scheduled' | 'live' | 'ended'
 */
export const getSessionsByStatus = async (status: string): Promise<LiveSession[]> => {
  const { data, error } = await supabase
    .from('bilibili_live_sessions')
    .select('*')
    .eq('status', status)
    .order('created_at', { ascending: false });

  if (error) {
    console.error(`获取${status}状态场次失败:`, error);
    throw error;
  }

  return data || [];
};

/**
 * 获取单个场次详情
 * @param sessionId 场次ID
 */
export const getSessionById = async (sessionId: string): Promise<LiveSession | null> => {
  const { data, error } = await supabase
    .from('bilibili_live_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();

  if (error) {
    console.error('获取场次详情失败:', error);
    throw error;
  }

  return data;
};

/**
 * 创建新的直播场次
 * @param formData 表单数据
 */
export const createSession = async (formData: AddSessionForm): Promise<LiveSession> => {
  try {
    // 步骤1: 爬取B站直播间信息（Mock后端API）
    const roomInfo = await scrapeRoomInfo(formData.room_url);

    // 步骤2: 检查房间信息是否已存在，不存在则创建
    const { data: existingRoom } = await supabase
      .from('bilibili_room_info')
      .select('*')
      .eq('room_id', roomInfo.room_id)
      .single();

    if (!existingRoom) {
      // 保存房间信息到数据库
      const { error: roomError } = await supabase
        .from('bilibili_room_info')
        .insert([roomInfo]);

      if (roomError) {
        console.error('保存房间信息失败:', roomError);
        throw roomError;
      }
    }

    // 步骤3: 创建新的直播场次
    const newSession = {
      room_id: roomInfo.room_id,
      streamer_name: roomInfo.streamer_name,
      room_title: roomInfo.room_title,
      cover_url: roomInfo.cover_url,
      scheduled_start_time: formData.scheduled_start_time || null,
      status: 'scheduled',
      is_recording: false,
      danmaku_count: 0,
      screenshot_count: 0,
      video_segment_count: 0,
    };

    const { data, error } = await supabase
      .from('bilibili_live_sessions')
      .insert([newSession])
      .select()
      .single();

    if (error) {
      console.error('创建场次失败:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('创建场次流程失败:', error);
    throw error;
  }
};

/**
 * 完成录制（结束整个场次，不可恢复）
 * @param sessionId 场次ID
 */
export const completeSession = async (sessionId: string): Promise<void> => {
  try {
    // 计算直播时长
    const session = await getSessionById(sessionId);
    let durationSeconds = 0;
    if (session?.actual_start_time) {
      const startTime = new Date(session.actual_start_time).getTime();
      const endTime = new Date().getTime();
      durationSeconds = Math.floor((endTime - startTime) / 1000);
    }

    // 更新场次状态为 'ended'（完成，不可恢复）
    // CDP 后端会监听此状态变化并自动停止采集
    const { error } = await supabase
      .from('bilibili_live_sessions')
      .update({
        status: 'ended',
        is_recording: false,
        end_time: new Date().toISOString(),
        duration_seconds: durationSeconds,
        updated_at: new Date().toISOString(),
      })
      .eq('id', sessionId);

    if (error) {
      console.error('完成场次失败:', error);
      throw error;
    }

    console.log('✅ 场次已标记为结束，CDP 后端会自动停止采集');
  } catch (error) {
    console.error('完成场次失败:', error);
    throw error;
  }
};

/**
 * 删除场次
 * @param sessionId 场次ID
 */
export const deleteSession = async (sessionId: string): Promise<void> => {
  const { error } = await supabase
    .from('bilibili_live_sessions')
    .delete()
    .eq('id', sessionId);

  if (error) {
    console.error('删除场次失败:', error);
    throw error;
  }
};

// ============= 弹幕相关 =============

/**
 * 获取场次的弹幕列表
 * @param sessionId 场次ID
 * @param limit 数量限制
 * @param offset 偏移量
 */
export const getDanmakuBySession = async (
  sessionId: string,
  limit: number = 100,
  offset: number = 0
): Promise<Danmaku[]> => {
  const { data, error } = await supabase
    .from('bilibili_danmaku')
    .select('*')
    .eq('session_id', sessionId)
    .eq('message_type', 'danmaku')
    .order('timestamp', { ascending: true })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('获取弹幕列表失败:', error);
    throw error;
  }

  return data || [];
};

/**
 * 获取场次弹幕数量
 * @param sessionId 场次ID
 */
export const getDanmakuCountBySession = async (sessionId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('bilibili_danmaku')
    .select('*', { count: 'exact', head: true })
    .eq('session_id', sessionId);

  if (error) {
    console.error('获取弹幕数量失败:', error);
    return 0;
  }

  return count || 0;
};

/**
 * 订阅场次弹幕实时更新（Realtime）
 * @param sessionId 场次ID
 * @param onNewDanmaku 新弹幕回调函数
 */
export const subscribeToSessionDanmaku = (
  sessionId: string,
  onNewDanmaku: (danmaku: Danmaku) => void
) => {
  const channel = supabase
    .channel(`session:${sessionId}:danmaku`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'bilibili_danmaku',
        filter: `session_id=eq.${sessionId},message_type=eq.danmaku`,
      },
      (payload) => {
        onNewDanmaku(payload.new as Danmaku);
      }
    )
    .subscribe();

  return channel;
};

// ============= 截图相关 =============

/**
 * 获取场次的截图列表
 * @param sessionId 场次ID
 */
export const getScreenshotsBySession = async (sessionId: string): Promise<Screenshot[]> => {
  const { data, error } = await supabase
    .from('bilibili_screenshots')
    .select('*')
    .eq('session_id', sessionId)
    .order('timestamp_in_session', { ascending: true });

  if (error) {
    console.error('获取截图列表失败:', error);
    throw error;
  }

  return data || [];
};

// ============= 数据导出相关 =============

/**
 * 导出场次弹幕为文本文件
 * @param sessionId 场次ID
 * @param sessionTitle 场次标题
 */
export const exportSessionDanmakuToText = async (
  sessionId: string,
  sessionTitle: string
): Promise<void> => {
  const { data, error } = await supabase
    .from('bilibili_danmaku')
    .select('*')
    .eq('session_id', sessionId)
    .order('timestamp', { ascending: true });

  if (error) {
    console.error('导出弹幕失败:', error);
    throw error;
  }

  if (!data || data.length === 0) {
    throw new Error('没有数据可导出');
  }

  // 生成文本内容
  let content = `# ${sessionTitle} - 弹幕导出\n`;
  content += `# 导出时间: ${new Date().toLocaleString('zh-CN')}\n`;
  content += `# 总计: ${data.length} 条弹幕\n\n`;
  content += '='.repeat(80) + '\n\n';

  data.forEach((danmaku) => {
    const time = new Date(danmaku.timestamp).toLocaleString('zh-CN');
    const userName = danmaku.user_name || '未知用户';
    const medal = danmaku.medal_name
      ? ` [${danmaku.medal_name} Lv${danmaku.medal_level}]`
      : '';
    content += `[${time}] ${userName}${medal}: ${danmaku.message}\n`;
  });

  // 下载文件
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sessionTitle}_弹幕_${Date.now()}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 导出场次弹幕为 CSV 文件
 * @param sessionId 场次ID
 * @param sessionTitle 场次标题
 */
export const exportSessionDanmakuToCSV = async (
  sessionId: string,
  sessionTitle: string
): Promise<void> => {
  const { data, error } = await supabase
    .from('bilibili_danmaku')
    .select('*')
    .eq('session_id', sessionId)
    .order('timestamp', { ascending: true });

  if (error) {
    console.error('导出弹幕失败:', error);
    throw error;
  }

  if (!data || data.length === 0) {
    throw new Error('没有数据可导出');
  }

  // 生成 CSV 内容
  const headers = [
    'ID',
    '场次ID',
    '房间ID',
    '弹幕内容',
    '用户名',
    '用户ID',
    '时间戳',
    '场次内时间',
    '消息类型',
    '颜色类型',
    '勋章名称',
    '勋章等级',
    '采集时间',
  ];

  let csvContent = '\uFEFF'; // UTF-8 BOM
  csvContent += headers.join(',') + '\n';

  data.forEach((danmaku) => {
    const row = [
      danmaku.id,
      danmaku.session_id,
      danmaku.room_id,
      `"${(danmaku.message || '').replace(/"/g, '""')}"`,
      `"${(danmaku.user_name || '').replace(/"/g, '""')}"`,
      danmaku.user_id || '',
      danmaku.timestamp || '',
      danmaku.timestamp_in_session || '',
      danmaku.message_type || '',
      danmaku.color_type || '',
      `"${(danmaku.medal_name || '').replace(/"/g, '""')}"`,
      danmaku.medal_level || '',
      danmaku.created_at,
    ];
    csvContent += row.join(',') + '\n';
  });

  // 下载文件
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sessionTitle}_弹幕_${Date.now()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ============= Cookie 管理相关 =============

/**
 * 保存 Cookie 到数据库
 * @param cookieJson Cookie JSON 字符串
 */
export const saveCookieToDatabase = async (cookieJson: string): Promise<void> => {
  // 验证 JSON 格式
  const cookies = JSON.parse(cookieJson);
  if (!Array.isArray(cookies)) {
    throw new Error('Cookie 必须是数组格式');
  }

  // 计算过期时间（假设 Cookie 有效期 14 天）
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 14);

  // 先将所有现有 Cookie 设置为非激活
  await supabase
    .from('bilibili_cookies')
    .update({ is_active: false })
    .eq('is_active', true);

  // 插入新 Cookie
  const { error } = await supabase.from('bilibili_cookies').insert([
    {
      user_identifier: 'default',
      cookies_json: cookieJson,
      expires_at: expiresAt.toISOString(),
      is_active: true,
      last_validated_at: new Date().toISOString(),
    },
  ]);

  if (error) {
    console.error('保存 Cookie 失败:', error);
    throw new Error('保存 Cookie 失败');
  }
};

/**
 * 获取当前激活的 Cookie
 */
export const getActiveCookie = async (): Promise<any | null> => {
  const { data, error } = await supabase
    .from('bilibili_cookies')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('获取 Cookie 失败:', error);
    return null;
  }

  return data;
};

export interface ActiveCookieEvidence {
  id: string;
  is_active: boolean;
  expires_at: string | null;
  last_validated_at: string | null;
  created_at: string | null;
}

/**
 * 获取首页状态台所需的 Cookie 元数据，不读取 cookies_json。
 */
export const getActiveCookieEvidence = async (): Promise<ActiveCookieEvidence | null> => {
  const { data, error } = await supabase
    .from('bilibili_cookies')
    .select('id, is_active, expires_at, last_validated_at, created_at')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error?.code === 'PGRST116') return null;

  if (error) {
    console.error('获取 Cookie 状态失败:', error);
    throw error;
  }

  return data as ActiveCookieEvidence;
};

/**
 * 验证结果接口
 */
export interface CookieValidationResult {
  isValid: boolean;
  message: string;
  validationType: 'bilibili_api' | 'database_only' | 'api_error';
  details?: {
    cookieCount?: number;
    validationDuration?: string;
    validatedAt?: string;
    method?: string;
    error?: string;
    suggestion?: string;
  };
}

/**
 * 数据库本地验证（快速检查）
 */
export const validateCookieLocal = async (): Promise<CookieValidationResult> => {
  try {
    const cookie = await getActiveCookie();
    if (!cookie) {
      return {
        isValid: false,
        message: '❌ 数据库中未找到Cookie',
        validationType: 'database_only',
        details: {
          suggestion: '请先扫码登录获取Cookie'
        }
      };
    }

    // 检查是否过期
    if (cookie.expires_at) {
      const expiresAt = new Date(cookie.expires_at);
      if (expiresAt < new Date()) {
        // 过期了，设置为非激活
        await supabase
          .from('bilibili_cookies')
          .update({ is_active: false })
          .eq('id', cookie.id);
        return {
          isValid: false,
          message: '❌ Cookie已过期',
          validationType: 'database_only',
          details: {
            validatedAt: new Date().toISOString(),
            suggestion: '请重新扫码登录'
          }
        };
      }
    }

    return {
      isValid: true,
      message: '✅ 数据库验证通过（Cookie存在且未过期）',
      validationType: 'database_only',
      details: {
        validatedAt: new Date().toISOString(),
        suggestion: '建议进行B站真实验证以确保登录状态有效'
      }
    };
  } catch (error) {
    console.error('数据库验证失败:', error);
    return {
      isValid: false,
      message: '❌ 数据库验证异常',
      validationType: 'database_only',
      details: {
        error: error instanceof Error ? error.message : '未知错误'
      }
    };
  }
};

/**
 * B站真实验证（调用后端API访问B站）
 */
export const validateCookie = async (): Promise<CookieValidationResult> => {
  try {
    // 构建 API 路径（兼容代理模式）
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const apiPath = API_BASE_URL.includes('/api/cookie-proxy')
      ? `${API_BASE_URL}?path=api/cookie/validate-real`
      : `${API_BASE_URL}/api/cookie/validate-real`;

    console.log('[ValidateCookie] 开始B站真实验证...');
    const startTime = Date.now();

    const response = await fetch(apiPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    const duration = Date.now() - startTime;

    console.log(`[ValidateCookie] B站验证完成，耗时: ${duration}ms`);
    console.log('[ValidateCookie] 验证结果:', result);

    // 如果验证成功，更新数据库中的last_validated_at
    if (result.isValid) {
      const cookie = await getActiveCookie();
      if (cookie) {
        await supabase
          .from('bilibili_cookies')
          .update({ last_validated_at: new Date().toISOString() })
          .eq('id', cookie.id);
      }
    }

    return {
      isValid: result.isValid,
      message: result.message || (result.isValid ? '验证成功' : '验证失败'),
      validationType: result.validationType || 'bilibili_api',
      details: result.details
    };
  } catch (error) {
    console.error('[ValidateCookie] B站API验证失败:', error);

    // 后端API失败，回退到数据库本地验证
    const localResult = await validateCookieLocal();

    return {
      ...localResult,
      message: `⚠️ 无法连接后端验证服务，已回退到数据库本地验证\n${localResult.message}`,
      details: {
        ...localResult.details,
        error: error instanceof Error ? error.message : '后端服务连接失败',
        suggestion: '后端服务可能未启动或网络连接异常。数据库验证通过不代表B站登录有效，建议修复后端服务后重新验证。'
      }
    };
  }
};

// ============= 统计数据相关 =============

/**
 * 获取场次的统计数据（用于趋势图）
 */
export const getStatsData = async (sessionId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('bilibili_danmaku')
    .select('*')
    .eq('session_id', sessionId)
    .eq('message_type', 'stats')
    .order('timestamp', { ascending: true });

  if (error) {
    console.error('获取统计数据失败:', error);
    return [];
  }

  return data || [];
};

// ============= 数据导出相关 =============

/**
 * 获取指定时间范围内的场次数据用于导出
 * @param days 天数（7, 15, 30）
 */
export const getSessionsDataForExport = async (days: number): Promise<SessionExportData[]> => {
  try {
    // 计算开始时间
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startDateISO = startDate.toISOString();

    // 查询指定时间范围内的场次（基于actual_start_time）
    const { data: sessions, error: sessionsError } = await supabase
      .from('bilibili_live_sessions')
      .select('*')
      .gte('actual_start_time', startDateISO)
      .not('actual_start_time', 'is', null)
      .order('actual_start_time', { ascending: false });

    if (sessionsError) {
      console.error('查询场次失败:', sessionsError);
      throw sessionsError;
    }

    if (!sessions || sessions.length === 0) {
      return [];
    }

    // 为每个场次聚合统计数据
    const exportData: SessionExportData[] = await Promise.all(
      sessions.map(async (session) => {
        // 计算时长（分钟）
        let duration_minutes = 0;
        if (session.actual_start_time && session.end_time) {
          const start = new Date(session.actual_start_time).getTime();
          const end = new Date(session.end_time).getTime();
          duration_minutes = Math.round((end - start) / 1000 / 60);
        } else if (session.duration_seconds) {
          duration_minutes = Math.round(session.duration_seconds / 60);
        }

        // 查询弹幕统计数据
        const { count: danmakuCount } = await supabase
          .from('bilibili_danmaku')
          .select('*', { count: 'exact', head: true })
          .eq('session_id', session.id)
          .eq('message_type', 'danmaku');

        // 查询互动用户数（去重）
        const { data: uniqueUsers } = await supabase
          .from('bilibili_danmaku')
          .select('user_id')
          .eq('session_id', session.id)
          .eq('message_type', 'danmaku')
          .not('user_id', 'is', null);

        const danmaku_users = uniqueUsers
          ? new Set(uniqueUsers.map((u) => u.user_id)).size
          : 0;

        // 查询统计数据（stats类型）
        const { data: statsData } = await supabase
          .from('bilibili_danmaku')
          .select('watched_count, like_count, online_count')
          .eq('session_id', session.id)
          .eq('message_type', 'stats')
          .order('timestamp', { ascending: true });

        // 安全地解析数值（处理空字符串、小数字符串等情况）
        const safeParseNumber = (value: unknown): number => {
          if (value === null || value === undefined || value === '') {
            return 0;
          }
          const num = typeof value === 'string' ? parseFloat(value) : Number(value);
          // 检查是否为有效的有限数值
          if (!isFinite(num) || isNaN(num)) {
            return 0;
          }
          return num;
        };

        // 计算峰值和平均值
        let peak_watched_count = 0;
        let avg_watched_count = 0;
        let peak_like_count = 0;
        let peak_online_count = 0;
        let avg_online_count = 0;

        if (statsData && statsData.length > 0) {
          const watchedCounts = statsData
            .map((s) => safeParseNumber(s.watched_count))
            .filter((v) => v > 0);
          const likeCounts = statsData
            .map((s) => safeParseNumber(s.like_count))
            .filter((v) => v > 0);
          const onlineCounts = statsData
            .map((s) => safeParseNumber(s.online_count))
            .filter((v) => v > 0);

          if (watchedCounts.length > 0) {
            peak_watched_count = Math.round(Math.max(...watchedCounts));
            avg_watched_count = Math.round(
              watchedCounts.reduce((a, b) => a + b, 0) / watchedCounts.length
            );
          }

          if (likeCounts.length > 0) {
            peak_like_count = Math.round(Math.max(...likeCounts));
          }

          if (onlineCounts.length > 0) {
            peak_online_count = Math.round(Math.max(...onlineCounts));
            avg_online_count = Math.round(
              onlineCounts.reduce((a, b) => a + b, 0) / onlineCounts.length
            );
          }
        }

        return {
          id: session.id,
          room_id: session.room_id,
          streamer_name: session.streamer_name,
          room_title: session.room_title,
          actual_start_time: session.actual_start_time,
          end_time: session.end_time,
          duration_minutes,
          danmaku_count: danmakuCount || 0,
          danmaku_users,
          peak_watched_count,
          avg_watched_count,
          peak_like_count,
          peak_online_count,
          avg_online_count,
          screenshot_count: session.screenshot_count || 0,
          video_segment_count: session.video_segment_count || 0,
          is_replay: session.is_replay,
          status: session.status,
        };
      })
    );

    return exportData;
  } catch (error) {
    console.error('获取导出数据失败:', error);
    throw error;
  }
};

// ============= 舆情分析相关 =============

/**
 * 获取场次的舆情分析结果
 * @param sessionId 场次ID
 */
export const getSentimentAnalysis = async (sessionId: string): Promise<SentimentAnalysisResult | null> => {
  const { data, error } = await supabase
    .from('bilibili_sentiment_analysis')
    .select('*')
    .eq('session_id', sessionId)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('获取舆情分析结果失败:', error);
    throw error;
  }

  return data;
};

/**
 * 触发舆情分析
 * @param sessionId 场次ID
 */
export const triggerSentimentAnalysis = async (sessionId: string): Promise<{
  success: boolean;
  status: string;
  message: string;
  analysis_id?: string;
  error?: string;
}> => {
  try {
    // 构建 API 路径（兼容代理模式）
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const apiPath = API_BASE_URL.includes('/api/cookie-proxy')
      ? `${API_BASE_URL}?path=api/analysis/sentiment`
      : `${API_BASE_URL}/api/analysis/sentiment`;

    console.log('[SentimentAnalysis] 触发舆情分析...');
    console.log('[SentimentAnalysis] API 路径:', apiPath);

    const response = await fetch(apiPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });

    const result = await response.json();

    console.log('[SentimentAnalysis] 响应:', result);

    if (!response.ok) {
      return {
        success: false,
        status: 'error',
        message: result.error || '请求失败',
        error: result.error,
      };
    }

    return {
      success: result.success,
      status: result.status,
      message: result.message,
      analysis_id: result.analysis_id,
    };
  } catch (error) {
    console.error('[SentimentAnalysis] 触发分析失败:', error);
    return {
      success: false,
      status: 'error',
      message: error instanceof Error ? error.message : '网络请求失败',
      error: error instanceof Error ? error.message : '未知错误',
    };
  }
};

/**
 * 订阅舆情分析结果更新
 * @param sessionId 场次ID
 * @param onUpdate 更新回调
 */
export const subscribeToSentimentAnalysis = (
  sessionId: string,
  onUpdate: (analysis: SentimentAnalysisResult) => void
) => {
  const channel = supabase
    .channel(`sentiment:${sessionId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'bilibili_sentiment_analysis',
        filter: `session_id=eq.${sessionId}`,
      },
      (payload) => {
        console.log('[SentimentAnalysis] 收到更新:', payload);
        if (payload.new) {
          onUpdate(payload.new as SentimentAnalysisResult);
        }
      }
    )
    .subscribe();

  return channel;
};

// ============= 预约规则相关 =============

/**
 * 获取所有预约规则
 */
export const getScheduleRules = async (): Promise<ScheduleRule[]> => {
  const { data, error } = await supabase
    .from('bilibili_schedule_rules')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('获取预约规则列表失败:', error);
    throw error;
  }

  return data || [];
};

/**
 * 创建预约规则
 */
export const createScheduleRule = async (formData: CreateScheduleRuleForm): Promise<ScheduleRule> => {
  try {
    // 1. 先爬取房间信息获取 room_id
    const roomInfo = await scrapeRoomInfo(formData.room_url);

    // 2. 计算首次触发时间
    const nextTrigger = calculateNextTriggerTime(
      formData.days_of_week,
      formData.start_time,
      formData.advance_minutes || 1
    );

    // 3. 插入规则
    const { data, error } = await supabase
      .from('bilibili_schedule_rules')
      .insert([{
        room_id: roomInfo.room_id,
        room_url: formData.room_url,
        name: formData.name || null,
        days_of_week: formData.days_of_week,
        start_time: formData.start_time + ':00', // 补全秒
        advance_minutes: formData.advance_minutes || 1,
        is_enabled: true,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
        next_trigger_at: nextTrigger,
        ws_timeout_minutes: formData.ws_timeout_minutes ?? 5,
        min_duration_minutes: formData.min_duration_minutes ?? 10,
      }])
      .select()
      .single();

    if (error) {
      console.error('创建预约规则失败:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('创建预约规则流程失败:', error);
    throw error;
  }
};

/**
 * 更新预约规则
 */
export const updateScheduleRule = async (
  id: string,
  updates: Partial<ScheduleRule>
): Promise<ScheduleRule> => {
  // 如果更新了调度相关字段，重新计算下次触发时间
  if (updates.days_of_week || updates.start_time || updates.advance_minutes) {
    const { data: existing } = await supabase
      .from('bilibili_schedule_rules')
      .select('days_of_week, start_time, advance_minutes')
      .eq('id', id)
      .single();

    if (existing) {
      const daysOfWeek = updates.days_of_week || existing.days_of_week;
      const startTime = updates.start_time || existing.start_time;
      const advanceMinutes = updates.advance_minutes ?? existing.advance_minutes;

      updates.next_trigger_at = calculateNextTriggerTime(
        daysOfWeek,
        startTime.replace(':00', ''), // 去掉秒
        advanceMinutes
      );
    }
  }

  const { data, error } = await supabase
    .from('bilibili_schedule_rules')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('更新预约规则失败:', error);
    throw error;
  }

  return data;
};

/**
 * 删除预约规则
 */
export const deleteScheduleRule = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('bilibili_schedule_rules')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('删除预约规则失败:', error);
    throw error;
  }
};

/**
 * 切换预约规则启用状态
 */
export const toggleScheduleRule = async (id: string, enabled: boolean): Promise<void> => {
  const updates: Partial<ScheduleRule> = {
    is_enabled: enabled,
  };

  // 如果启用规则，重新计算下次触发时间
  if (enabled) {
    const { data: ruleData } = await supabase
      .from('bilibili_schedule_rules')
      .select('days_of_week, start_time, advance_minutes')
      .eq('id', id)
      .single();

    if (ruleData) {
      updates.next_trigger_at = calculateNextTriggerTime(
        ruleData.days_of_week,
        ruleData.start_time.replace(':00', ''),
        ruleData.advance_minutes
      );
    }
  }

  const { error } = await supabase
    .from('bilibili_schedule_rules')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) {
    console.error('切换预约规则状态失败:', error);
    throw error;
  }
};

/**
 * 计算下次触发时间（前端辅助函数）
 */
function calculateNextTriggerTime(
  daysOfWeek: number[],
  startTime: string,
  advanceMinutes: number
): string {
  const now = new Date();
  const [hours, minutes] = startTime.split(':').map(Number);
  const advanceMs = advanceMinutes * 60 * 1000;

  // 强制使用中国标准时间 (UTC+8) 进行计算
  // 逻辑：将当前时间 +8 小时，假装它是 UTC 时间进行日期计算
  const offsetMs = 8 * 60 * 60 * 1000;
  const cstNow = new Date(now.getTime() + offsetMs); // "中国时间对象"（其实是UTC时间平移）

  // 从今天开始找下一个符合条件的日期
  for (let daysAhead = 0; daysAhead <= 7; daysAhead++) {
    // 基于 cstNow 进行日期的加减
    const targetCst = new Date(cstNow);
    targetCst.setUTCDate(cstNow.getUTCDate() + daysAhead);
    targetCst.setUTCHours(hours, minutes, 0, 0);

    // 还原为真实 UTC 时间：(CST假时间) - 8小时 - 提前分钟数
    const realTimestamp = targetCst.getTime() - offsetMs - advanceMs;

    // 如果是今天但时间已过，跳过
    if (daysAhead === 0 && realTimestamp <= now.getTime()) {
      continue;
    }

    // 检查这一天是否在规则的星期中
    // 注意：使用 getUTCDay() 因为我们操作的是"假装是UTC的CST时间"
    const dayOfWeek = targetCst.getUTCDay() === 0 ? 7 : targetCst.getUTCDay();
    if (daysOfWeek.includes(dayOfWeek)) {
      return new Date(realTimestamp).toISOString();
    }
  }

  // 默认返回下周
  const fallbackCst = new Date(cstNow);
  fallbackCst.setUTCDate(cstNow.getUTCDate() + 7);
  fallbackCst.setUTCHours(hours, minutes, 0, 0);
  const fallbackTimestamp = fallbackCst.getTime() - offsetMs - advanceMs;
  return new Date(fallbackTimestamp).toISOString();
}
